import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Player } from './components/Player';
import { SearchBar, SearchResults } from './components/Search';
import { ShareModal } from './components/Share';
import { InfoModal, RssModal, ShowInfoModal } from './components/Info';
import { Sidebar } from './components/Sidebar';
import { Queue } from './components/Queue';
import { searchEpisodes, getEpisode, getNextEpisode, getRecentStationEpisodes, getStationBySlug, findEpisodeByTimestamp } from './api/client';
import { parseTimestamp } from './utils/time';
import { loadSavedPosition, clearSavedPosition, usePlaybackHistory, usePWAInstall, usePersistentStorage, usePWAMode, useShowNotifications, useSwipeToClose } from './hooks';
import { InstallPrompt } from './components/PWA';
import { ShowsPanel } from './pages/ShowsPage';
import { SchedulePanel } from './pages/SchedulePage';
import { useStationSlug } from './contexts/StationSlugContext';
import { HiXMark, HiBell, HiSquares2X2 } from 'react-icons/hi2';
import type { Episode, Show, Stream, SearchResult, RecentEpisode } from './types';

// LocalStorage key prefix for queue (per-station)
const QUEUE_STORAGE_PREFIX = 'autopod-player-queue-';

// Image prefetch cache - tracks URLs we've already prefetched
const prefetchedImages = new Set<string>();

// Prefetch an image so it's in the browser cache
function prefetchImage(url: string): Promise<void> {
  if (!url || prefetchedImages.has(url)) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      prefetchedImages.add(url);
      resolve();
    };
    img.onerror = () => resolve(); // Don't fail on error, just continue
    img.src = url;
  });
}

// Prefetch multiple images
function prefetchImages(urls: string[]): void {
  urls.filter(Boolean).forEach(url => prefetchImage(url));
}

// Deduplicate array by episodeid
function dedupeQueue(queue: RecentEpisode[]): RecentEpisode[] {
  const seen = new Set<number>();
  return queue.filter(ep => {
    if (seen.has(ep.episodeid)) return false;
    seen.add(ep.episodeid);
    return true;
  });
}

// Load queue from localStorage for a specific stream
function loadQueueFromStorage(streamId: number | null): RecentEpisode[] {
  if (!streamId) return [];
  try {
    const stored = localStorage.getItem(`${QUEUE_STORAGE_PREFIX}${streamId}`);
    if (stored) {
      // Dedupe on load in case of corrupted data
      return dedupeQueue(JSON.parse(stored));
    }
  } catch (e) {
    console.error('Failed to load queue from storage:', e);
  }
  return [];
}

// Save queue to localStorage for a specific stream
function saveQueueToStorage(streamId: number | null, queue: RecentEpisode[]): void {
  if (!streamId) return;
  try {
    localStorage.setItem(`${QUEUE_STORAGE_PREFIX}${streamId}`, JSON.stringify(queue));
  } catch (e) {
    console.error('Failed to save queue to storage:', e);
  }
}

function App() {
  // Get episode ID from route params
  const { episodeId: episodeIdParam } = useParams<{ episodeId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Parse episode ID, start time, and autoplay flag
  const episodeId = episodeIdParam ? parseInt(episodeIdParam, 10) : null;
  const startTimeParam = searchParams.get('t');
  const urlStartTime = startTimeParam ? parseTimestamp(startTimeParam) : undefined;
  const shouldAutoplay = searchParams.get('autoplay') === '1';

  // Timestamp-based episode lookup (for highlight/clip linking)
  const timestampParam = searchParams.get('timestamp');
  const offsetParam = searchParams.get('offset');
  const endtimeParam = searchParams.get('endtime');
  const prerollParam = searchParams.get('preroll');
  const titleOverride = searchParams.get('title');
  const imageOverride = searchParams.get('image');

  // Effective start time: URL param takes priority, then saved position
  const [effectiveStartTime, setEffectiveStartTime] = useState<number | undefined>(undefined);

  // Pre-roll ad and endtime for highlight/clip mode
  const [prerollPlaying, setPrerollPlaying] = useState(false);
  const [prerollDone, setPrerollDone] = useState(!prerollParam); // true if no preroll
  const endTime = endtimeParam ? parseInt(endtimeParam, 10) : undefined;
  const prerollAudioRef = useRef<HTMLAudioElement | null>(null);

  // Detect current route
  const location = useLocation();
  const isShowsMode = location.pathname === '/shows';
  const isScheduleMode = location.pathname === '/schedule';
  const isPanelMode = isShowsMode || isScheduleMode; // Any overlay panel that keeps Player alive

  // Station slug from /app/{slug}/ path prefix (for per-station PWA)
  const appSlug = useStationSlug();

  // Live mode: ?live=true param, or root path in /app/{slug}/ PWA (not episode routes)
  const isOnRootPath = !episodeIdParam && !isShowsMode && !isScheduleMode;
  const isLiveModeFromUrl = !timestampParam && (searchParams.get('live') === 'true' || !!(appSlug && isOnRootPath));
  const isLiveMode = !isPanelMode && isLiveModeFromUrl;
  const liveStreamSlug = appSlug || searchParams.get('stream');
  const panelStreamSlug = isPanelMode ? (appSlug || searchParams.get('stream')) : null;

  // Track whether the Player was in live mode before entering a panel - preserves Player key
  const wasLiveModeRef = useRef(false);
  if (!isPanelMode) {
    wasLiveModeRef.current = isLiveModeFromUrl;
  }
  const playerIsLive = isPanelMode ? wasLiveModeRef.current : isLiveMode;

  // Episode data
  const [episode, setEpisode] = useState<Episode | null>(null);
  const [show, setShow] = useState<Show | null>(null);
  const [stream, setStream] = useState<Stream | null>(null);
  const [presenter, setPresenter] = useState<string>('');
  const [initialLoading, setInitialLoading] = useState(true); // Only for first load
  const [error, setError] = useState<string | null>(null);
  const [latestEpisodeId, setLatestEpisodeId] = useState<number | null>(null); // For live mode "On Demand" button

  // Background image transition state
  const [currentBgImage, setCurrentBgImage] = useState<string>('');
  const [nextBgImage, setNextBgImage] = useState<string>('');
  const [bgImageLoaded, setBgImageLoaded] = useState(false);
  const [isBgTransitioning, setIsBgTransitioning] = useState(false);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Modal state
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isRssOpen, setIsRssOpen] = useState(false);
  const [showInfoData, setShowInfoData] = useState<{ title: string; description?: string; image?: string } | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Mobile drawer state (with closing animation support)
  const [queueDrawerState, setQueueDrawerState] = useState<'closed' | 'open' | 'closing'>('closed');
  const [episodesDrawerState, setEpisodesDrawerState] = useState<'closed' | 'open' | 'closing'>('closed');

  const openQueueDrawer = useCallback(() => setQueueDrawerState('open'), []);
  const closeQueueDrawer = useCallback(() => {
    setQueueDrawerState('closing');
    setTimeout(() => setQueueDrawerState('closed'), 200); // Match animation duration
  }, []);

  const openEpisodesDrawer = useCallback(() => setEpisodesDrawerState('open'), []);
  const closeEpisodesDrawer = useCallback(() => {
    setEpisodesDrawerState('closing');
    setTimeout(() => setEpisodesDrawerState('closed'), 200); // Match animation duration
  }, []);

  // Swipe-to-close for mobile drawers
  const queueSwipe = useSwipeToClose({ direction: 'left', onClose: closeQueueDrawer });
  const episodesSwipe = useSwipeToClose({ direction: 'right', onClose: closeEpisodesDrawer });

  // Queue state - starts empty, loaded when stream is available
  const [queue, setQueue] = useState<RecentEpisode[]>([]);
  const [queueStreamId, setQueueStreamId] = useState<number | null>(null);

  // Playback history for "Continue Listening"
  const { history, updateHistory, removeFromHistory } = usePlaybackHistory(stream?.streamid ?? null);

  // PWA install prompt (station-specific dismiss key)
  const { isPromptVisible, dismissPrompt, install, isIOS } = usePWAInstall(appSlug || stream?.rssfilename);

  // Persistent storage for caching audio
  const { isPersisted, requestPersistence } = usePersistentStorage();

  // PWA mode detection
  const isPWAMode = usePWAMode();

  // Live show image override (show image when a show with artwork is on air)
  const [liveShowImage, setLiveShowImage] = useState<string | null>(null);

  // Currently live show tracking (for notifications)
  const [currentlyLiveShowId, setCurrentlyLiveShowId] = useState<number | null>(null);

  // Show notifications (PWA mode only)
  const { isFollowing, followShow, unfollowShow, followedShows } = useShowNotifications(
    stream?.streamid ?? null,
    currentlyLiveShowId,
    isPWAMode
  );

  // Handlers for following shows from Schedule component
  const handleFollowShow = useCallback((showId: number, showTitle: string, image?: string) => {
    if (!stream) return;
    followShow({
      showId,
      showTitle,
      streamId: stream.streamid,
      streamName: stream.streamname,
      image,
    });
  }, [stream, followShow]);

  const handleUnfollowShow = useCallback((showId: number) => {
    unfollowShow(showId);
  }, [unfollowShow]);

  // Load queue when stream changes
  useEffect(() => {
    if (stream && stream.streamid !== queueStreamId) {
      setQueueStreamId(stream.streamid);
      setQueue(loadQueueFromStorage(stream.streamid));
    }
  }, [stream, queueStreamId]);

  // Persist queue to localStorage whenever it changes (for current stream)
  useEffect(() => {
    if (queueStreamId) {
      saveQueueToStorage(queueStreamId, queue);
    }
  }, [queue, queueStreamId]);

  // Prefetch images for queue items so transitions are smooth
  useEffect(() => {
    if (queue.length > 0 && stream) {
      const imageUrls = queue.map(ep => ep.image || stream.image);
      prefetchImages(imageUrls);
    }
  }, [queue, stream]);

  // Update document title, favicon, theme color, and PWA manifest based on stream branding
  useEffect(() => {
    if (!stream) return;

    // Update document title
    document.title = isShowsMode
      ? `Shows - ${stream.streamname}`
      : isScheduleMode
        ? `Schedule - ${stream.streamname}`
        : `${stream.streamname} `;

    // Update favicon to stream image
    const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (favicon && stream.image) {
      favicon.href = stream.image;
    }

    // Update apple-touch-icon
    const appleTouchIcon = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
    if (appleTouchIcon && stream.image) {
      appleTouchIcon.href = stream.image;
    }

    // Update theme color (player_color overrides stream color)
    const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const effectiveThemeColor = stream.player_color || stream.color;
    if (themeColor && effectiveThemeColor) {
      themeColor.content = effectiveThemeColor;
    }

    // Update PWA manifest to station-specific manifest (only if PWA is enabled)
    if (stream.enable_pwa !== false) {
      let manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
      if (!manifestLink) {
        manifestLink = document.createElement('link');
        manifestLink.rel = 'manifest';
        document.head.appendChild(manifestLink);
      }
      manifestLink.href = `/api/station/${stream.streamid}/manifest.json`;
    } else {
      // Remove manifest link if PWA is disabled
      const manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
      if (manifestLink) {
        manifestLink.remove();
      }
    }

    // Apply theme mode (dark/light/system)
    if (stream.theme_mode === 'dark' || stream.theme_mode === 'light') {
      document.documentElement.setAttribute('data-theme', stream.theme_mode);
    } else {
      document.documentElement.removeAttribute('data-theme');
    }

    // Set background tint as CSS variable for global use
    const tint = stream.background_tint || stream.player_color || stream.color || '#6366f1';
    document.documentElement.style.setProperty('--color-tint', tint);

    // Set fallback background for transparent station/episode artwork
    const imageBackground = stream.player_color || stream.color || '#6366f1';
    document.documentElement.style.setProperty('--color-image-bg', imageBackground);

    // Cleanup: restore defaults when component unmounts
    return () => {
      document.title = 'Player';
      document.documentElement.removeAttribute('data-theme');
      document.documentElement.style.removeProperty('--color-tint');
      document.documentElement.style.removeProperty('--color-image-bg');
    };
  }, [stream, isShowsMode, isScheduleMode]);

  // Clear live show image when leaving live mode
  useEffect(() => {
    if (!isLiveMode) {
      setLiveShowImage(null);
    }
  }, [isLiveMode]);

  // Handle background image transitions - layer-based crossfade
  // Keep current image visible at all times, fade in next on top, then swap
  useEffect(() => {
    if (!episode || !stream) return;

    // In live mode, prefer the current show's image if available
    // In clip/highlight mode, prefer the image override
    const newImageUrl = imageOverride
      || (isLiveMode && liveShowImage
        ? liveShowImage
        : episode.episodeimage || episode.image || stream.image);

    // If no current image, set it directly (initial load)
    if (!currentBgImage) {
      setCurrentBgImage(newImageUrl);
      setBgImageLoaded(true);
      return;
    }

    // If same image, do nothing
    if (newImageUrl === currentBgImage) return;

    // Already transitioning to this image? Skip
    if (newImageUrl === nextBgImage) return;

    // Start transition: preload new image
    setIsBgTransitioning(true);
    setBgImageLoaded(false);
    setNextBgImage(newImageUrl);

    const img = new Image();
    img.onload = () => {
      // Image loaded, fade it in on top of current
      setBgImageLoaded(true);
      // After fade completes, swap images (next becomes current)
      setTimeout(() => {
        setCurrentBgImage(newImageUrl);
        setNextBgImage('');
        setIsBgTransitioning(false);
      }, 600); // Slightly longer than CSS transition to ensure complete
    };
    img.onerror = () => {
      // Fallback to stream image on error
      setCurrentBgImage(stream.image);
      setNextBgImage('');
      setBgImageLoaded(true);
      setIsBgTransitioning(false);
    };
    img.src = newImageUrl;
  }, [episode, stream, currentBgImage, nextBgImage, isLiveMode, liveShowImage]);

  // Load episode or live stream data when route changes
  useEffect(() => {
    // Panel mode (shows/schedule): load stream if not already loaded, preserve existing playback state
    if (isPanelMode && panelStreamSlug) {
      async function loadPanelStream(slug: string) {
        // If we already have stream data (e.g., came from player), keep it and just stop loading
        if (stream) {
          setInitialLoading(false);
          return;
        }
        // No stream data yet - need to load from slug
        setInitialLoading(true);
        setError(null);
        try {
          const data = await getStationBySlug(slug);
          await prefetchImage(data.stream.image);
          // Create minimal placeholder episode/show so the app doesn't render the error state
          const placeholderEpisode: Episode = {
            episodeid: 0,
            episodetitle: data.stream.streamname,
            datetime: Math.floor(Date.now() / 1000),
            fileurl: '',
            image: data.stream.image,
          };
          const placeholderShow: Show = {
            showid: 0,
            title: data.stream.streamname,
            published: true,
            streamid: data.stream.streamid,
          };
          setEpisode(placeholderEpisode);
          setShow(placeholderShow);
          setStream(data.stream);
          setPresenter('');
          setInitialLoading(false);
        } catch {
          setError('Failed to load station');
          setInitialLoading(false);
        }
      }
      loadPanelStream(panelStreamSlug);
      return;
    }

    // Live mode: ?live=true&stream=[slug]
    if (isLiveMode && liveStreamSlug) {
      async function loadLiveStream(slug: string) {
        setInitialLoading(true);
        setError(null);

        try {
          const data = await getStationBySlug(slug);

          // Guard: if live mode is disabled for this station, redirect to on-demand
          if (data.stream.live_enabled === false) {
            try {
              const recentEpisodes = await getRecentStationEpisodes(data.stream.streamid, 1);
              if (recentEpisodes.length > 0) {
                navigate(`/${recentEpisodes[0].episodeid}`, { replace: true });
                return;
              }
            } catch { /* fall through to error */ }
            setError('Live stream is not available for this station');
            setInitialLoading(false);
            return;
          }

          // Preload the stream image
          await prefetchImage(data.stream.image);

          // Check if stream has a live URL (prefer player live_url, fall back to stream url)
          const liveStreamUrl = data.stream.live_url || data.stream.url;
          if (!liveStreamUrl) {
            setError('Live stream not available for this station');
            setInitialLoading(false);
            return;
          }

          // Create a "live" episode placeholder for the Player
          const liveEpisode: Episode = {
            episodeid: 0, // Special ID for live
            episodetitle: `Live - ${data.stream.streamname}`,
            datetime: Math.floor(Date.now() / 1000),
            fileurl: liveStreamUrl, // Use the live stream URL
            image: data.stream.image,
          };

          // Create a placeholder show for live
          const liveShow: Show = {
            showid: 0,
            title: data.stream.streamname,
            published: true,
            streamid: data.stream.streamid,
          };

          setEpisode(liveEpisode);
          setShow(liveShow);
          setStream(data.stream);
          setPresenter('');

          // Fetch latest episode for "On Demand" button
          try {
            const recentEpisodes = await getRecentStationEpisodes(data.stream.streamid, 1);
            if (recentEpisodes.length > 0) {
              setLatestEpisodeId(recentEpisodes[0].episodeid);
            }
          } catch (err) {
            console.error('Failed to fetch latest episode for On Demand:', err);
          }

          setInitialLoading(false);
        } catch {
          setError('Failed to load live stream');
          setInitialLoading(false);
        }
      }

      loadLiveStream(liveStreamSlug);
      return;
    }

    // Panel mode without stream slug
    if (isPanelMode) {
      setError('No station specified');
      setInitialLoading(false);
      return;
    }

    // Timestamp-based episode lookup (highlight/clip linking)
    // URL: /station/{streamid}?timestamp=EPOCH&offset=SECS&endtime=SECS&preroll=URL
    // Must be checked BEFORE the "redirect to latest episode" fallback
    if (timestampParam && !episodeId && liveStreamSlug) {
      async function resolveTimestamp(slug: string) {
        setInitialLoading(true);
        setError(null);
        try {
          const data = await getStationBySlug(slug);
          const ts = parseInt(timestampParam!, 10);
          const off = offsetParam ? parseInt(offsetParam, 10) : 0;
          const result = await findEpisodeByTimestamp(data.stream.streamid, ts, off);

          // Build the redirect URL preserving endtime and preroll params
          const params = new URLSearchParams();
          params.set('t', String(result.seek));
          params.set('autoplay', '1');
          if (endtimeParam) params.set('endtime', endtimeParam);
          if (prerollParam) params.set('preroll', prerollParam);
          if (titleOverride) params.set('title', titleOverride);
          if (imageOverride) params.set('image', imageOverride);

          const redirectUrl = `/${result.episodeid}?${params.toString()}`;
          console.log('[Timestamp] Redirecting to:', redirectUrl);
          navigate(redirectUrl, { replace: true });
        } catch {
          setError('Could not find an episode for this time');
          setInitialLoading(false);
        }
      }
      resolveTimestamp(liveStreamSlug);
      return;
    }

    // No episode ID but stream slug provided — redirect to latest episode
    if (!episodeId && liveStreamSlug) {
      async function loadLatestEpisode(slug: string) {
        setInitialLoading(true);
        setError(null);
        try {
          const data = await getStationBySlug(slug);
          const recentEpisodes = await getRecentStationEpisodes(data.stream.streamid, 1);
          if (recentEpisodes.length > 0) {
            navigate(`/${recentEpisodes[0].episodeid}`, { replace: true });
          } else {
            setError('No episodes available for this station');
            setInitialLoading(false);
          }
        } catch {
          setError('Failed to load station');
          setInitialLoading(false);
        }
      }
      loadLatestEpisode(liveStreamSlug);
      return;
    }

    // Regular episode mode
    if (!episodeId) {
      setError('No episode ID in URL');
      setInitialLoading(false);
      return;
    }

    async function loadEpisode(id: number) {
      // Only show full loading screen if we don't have any episode yet
      if (!episode) {
        setInitialLoading(true);
      }
      setError(null);

      try {
        const data = await getEpisode(id);

        // Preload the episode image before showing content
        // This ensures smooth transitions without flicker
        const imageUrl = data.episode.episodeimage || data.episode.image || data.stream.image;
        await prefetchImage(imageUrl);

        // Determine effective start time: URL param > saved position
        // Only use saved position if no URL param was provided
        if (urlStartTime !== undefined) {
          setEffectiveStartTime(urlStartTime);
        } else {
          const savedPosition = loadSavedPosition(id);
          setEffectiveStartTime(savedPosition ?? undefined);
        }

        // Now that image is cached, update state
        setEpisode(data.episode);
        setShow(data.show);
        setStream(data.stream);
        setPresenter(data.episode.metavalue || '');

        // Add current episode to queue if not already there (at end, not top)
        const currentEpisodeForQueue: RecentEpisode = {
          episodeid: data.episode.episodeid,
          episodetitle: data.episode.episodetitle,
          datetime: data.episode.datetime,
          image: data.episode.episodeimage || data.episode.image,
          showtitle: data.show.title,
          showid: data.show.showid,
        };

        setQueue(prev => dedupeQueue([...prev, currentEpisodeForQueue]));

        setInitialLoading(false);
      } catch {
        setError('Failed to load episode');
        setInitialLoading(false);
      }
    }

    loadEpisode(episodeId);
  }, [episodeId, isLiveMode, liveStreamSlug, isPanelMode, panelStreamSlug, urlStartTime]);

  // Pre-roll ad: user taps play, ad plays, then episode starts
  const startPreroll = useCallback(() => {
    if (!prerollParam || prerollDone) return;

    const prerollAudio = new Audio(prerollParam);
    prerollAudioRef.current = prerollAudio;
    setPrerollPlaying(true);

    prerollAudio.addEventListener('ended', () => {
      setPrerollPlaying(false);
      setPrerollDone(true);
      prerollAudioRef.current = null;
    });

    prerollAudio.addEventListener('error', () => {
      setPrerollPlaying(false);
      setPrerollDone(true);
      prerollAudioRef.current = null;
    });

    prerollAudio.play().catch(() => {
      // Still blocked — skip pre-roll
      setPrerollPlaying(false);
      setPrerollDone(true);
      prerollAudioRef.current = null;
    });
  }, [prerollParam, prerollDone]);

  // Skip pre-roll handler (used by skip button if we add one)
  const _skipPreroll = useCallback(() => {
    if (prerollAudioRef.current) {
      prerollAudioRef.current.pause();
      prerollAudioRef.current.src = '';
      prerollAudioRef.current = null;
    }
    setPrerollPlaying(false);
    setPrerollDone(true);
  }, []);
  void _skipPreroll; // Preserved for future use

  // Feature gate guard: redirect away from /shows or /schedule if the feature is disabled
  useEffect(() => {
    if (!stream || !stream.rssfilename) return;
    if (isShowsMode && stream.show_shows !== true) {
      navigate(`/?live=true&stream=${stream.rssfilename}`, { replace: true });
    } else if (isScheduleMode && stream.show_schedule !== true) {
      navigate(`/?live=true&stream=${stream.rssfilename}`, { replace: true });
    }
  }, [stream, isShowsMode, isScheduleMode, navigate]);

  // SPA navigation helper
  const navigateToEpisode = useCallback((id: number) => {
    console.log('navigateToEpisode called with id:', id);
    // Close search results when navigating
    setShowResults(false);
    setSearchTerm('');
    navigate(`/${id}`);
    console.log('navigate() called, should be SPA transition');
  }, [navigate]);

  // Debounced search
  const doSearch = useCallback(async () => {
    if (!stream) return;

    setIsSearching(true);
    setShowResults(true);

    try {
      const results = await searchEpisodes(stream.streamid, searchTerm);
      setSearchResults(results);
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [stream, searchTerm]);

  // Handle search input change with debounce
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value === '') {
      setShowResults(false);
      setSearchResults([]);
      return;
    }

    if (value.length > 1) {
      searchTimeoutRef.current = setTimeout(() => {
        doSearch();
      }, 500);
    }
  }, [doSearch]);

  // Focus search handler for keyboard shortcut
  const handleFocusSearch = useCallback(() => {
    searchInputRef.current?.focus();
  }, []);

  // Close search results
  const handleCloseResults = useCallback(() => {
    setShowResults(false);
  }, []);

  // Queue handlers
  const handleAddToQueue = useCallback((episode: RecentEpisode) => {
    setQueue(prev => dedupeQueue([...prev, episode]));
  }, []);

  // Helper to add episode to queue with deduplication
  const addToQueueSafe = useCallback((ep: RecentEpisode) => {
    setQueue(prev => dedupeQueue([...prev, ep]));
  }, []);

  const handleRemoveFromQueue = useCallback((episodeId: number) => {
    setQueue(prev => prev.filter(ep => ep.episodeid !== episodeId));
  }, []);

  const handlePlayFromQueue = useCallback((episodeId: number) => {
    navigateToEpisode(episodeId);
  }, [navigateToEpisode]);

  const handleClearQueue = useCallback(() => {
    setQueue([]);
  }, []);

  const handleReorderQueue = useCallback((newQueue: RecentEpisode[]) => {
    setQueue(newQueue);
  }, []);

  // Navigate to episode with autoplay flag
  const navigateToEpisodeWithAutoplay = useCallback((id: number) => {
    console.log('navigateToEpisodeWithAutoplay called with id:', id);
    setShowResults(false);
    setSearchTerm('');
    navigate(`/${id}?autoplay=1`);
  }, [navigate]);

  // Queue navigation
  const currentQueueIndex = episode ? queue.findIndex(ep => ep.episodeid === episode.episodeid) : -1;
  const hasPrevTrack = currentQueueIndex > 0;
  const hasNextInQueue = currentQueueIndex >= 0 && currentQueueIndex < queue.length - 1;

  const handlePrevTrack = useCallback(() => {
    if (hasPrevTrack && episode) {
      const prevEpisode = queue[currentQueueIndex - 1];
      navigateToEpisode(prevEpisode.episodeid);
    }
  }, [queue, currentQueueIndex, hasPrevTrack, episode, navigateToEpisode]);

  // Next track - plays next from queue, or fetches next chronological episode
  const handleNextTrack = useCallback(async () => {
    if (!episode || !stream) return;

    // If there's a next item in queue, play it
    if (hasNextInQueue) {
      const nextEpisode = queue[currentQueueIndex + 1];
      navigateToEpisodeWithAutoplay(nextEpisode.episodeid);
      return;
    }

    // Otherwise, fetch next chronological episode (same as autoplay)
    try {
      const response = await getNextEpisode(episode.episodeid);
      if (response.episode) {
        // Add to queue and navigate
        const nextEp: RecentEpisode = {
          episodeid: response.episode.episodeid,
          episodetitle: response.episode.episodetitle,
          datetime: response.episode.datetime,
          image: response.episode.image,
          showtitle: response.episode.showtitle,
          showid: response.episode.showid,
        };
        addToQueueSafe(nextEp);
        navigateToEpisodeWithAutoplay(response.episode.episodeid);
      } else {
        // No more episodes from API - try loading more from station
        console.log('No more episodes from current show/station, fetching station episodes...');
        const stationEpisodes = await getRecentStationEpisodes(stream.streamid, 20);
        // Find an episode we haven't played yet (not in queue)
        const queueIds = new Set(queue.map(ep => ep.episodeid));
        const unplayedEpisode = stationEpisodes.find(ep => !queueIds.has(ep.episodeid) && ep.episodeid !== episode.episodeid);
        if (unplayedEpisode) {
          addToQueueSafe(unplayedEpisode);
          navigateToEpisodeWithAutoplay(unplayedEpisode.episodeid);
        } else {
          console.log('No more episodes available on station');
        }
      }
    } catch (err) {
      console.error('Failed to fetch next episode:', err);
    }
  }, [episode, stream, hasNextInQueue, queue, currentQueueIndex, navigateToEpisodeWithAutoplay, addToQueueSafe]);

  // Autoplay handler - called when episode ends
  const handleEpisodeEnded = useCallback(async () => {
    if (!episode || !stream) return;

    // Clear saved position since episode completed
    clearSavedPosition(episode.episodeid);

    // Check if there's a next item in the queue
    if (hasNextInQueue) {
      const nextEpisode = queue[currentQueueIndex + 1];
      navigateToEpisodeWithAutoplay(nextEpisode.episodeid);
      return;
    }

    // No queue items, fetch next chronological episode
    try {
      const response = await getNextEpisode(episode.episodeid);
      if (response.episode) {
        // Add to queue and navigate
        const nextEp: RecentEpisode = {
          episodeid: response.episode.episodeid,
          episodetitle: response.episode.episodetitle,
          datetime: response.episode.datetime,
          image: response.episode.image,
          showtitle: response.episode.showtitle,
          showid: response.episode.showid,
        };
        addToQueueSafe(nextEp);
        navigateToEpisodeWithAutoplay(response.episode.episodeid);
      } else {
        // No more episodes from API - try loading more from station
        console.log('Episode ended, no more from show/station, fetching more...');
        const stationEpisodes = await getRecentStationEpisodes(stream.streamid, 20);
        const queueIds = new Set(queue.map(ep => ep.episodeid));
        const unplayedEpisode = stationEpisodes.find(ep => !queueIds.has(ep.episodeid) && ep.episodeid !== episode.episodeid);
        if (unplayedEpisode) {
          addToQueueSafe(unplayedEpisode);
          navigateToEpisodeWithAutoplay(unplayedEpisode.episodeid);
        } else {
          console.log('All station content has been played');
        }
      }
    } catch (err) {
      console.error('Failed to fetch next episode for autoplay:', err);
    }
  }, [episode, stream, hasNextInQueue, queue, currentQueueIndex, navigateToEpisodeWithAutoplay, addToQueueSafe]);

  // Track current time and duration for share/info modals
  useEffect(() => {
    const handleTimeUpdate = () => {
      const audio = document.querySelector('audio');
      if (audio) {
        setCurrentTime(audio.currentTime);
      }
    };

    const handleDurationChange = () => {
      const audio = document.querySelector('audio');
      if (audio && audio.duration && !isNaN(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    const audio = document.querySelector('audio');
    if (audio) {
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('durationchange', handleDurationChange);
      audio.addEventListener('loadedmetadata', handleDurationChange);
      return () => {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('durationchange', handleDurationChange);
        audio.removeEventListener('loadedmetadata', handleDurationChange);
      };
    }
  }, [episode]);

  // Update playback history periodically (every 10 seconds)
  const lastHistoryUpdateRef = useRef(0);
  useEffect(() => {
    if (!episode || !show || isLiveMode) return;
    if (duration <= 0 || currentTime <= 0) return;

    const now = Date.now();
    // Only update every 10 seconds to avoid excessive writes
    if (now - lastHistoryUpdateRef.current < 10000) return;
    lastHistoryUpdateRef.current = now;

    const progress = currentTime / duration;
    // Only track if there's meaningful progress
    if (progress >= 0.01 && progress < 0.95) {
      updateHistory({
        episodeId: episode.episodeid,
        episodeTitle: episode.episodetitle,
        showTitle: show.title,
        image: episode.episodeimage || episode.image,
        progress,
        duration,
      });
    }
  }, [episode, show, isLiveMode, currentTime, duration, updateHistory]);

  // Request persistent storage on first play (so cached audio isn't evicted)
  const hasRequestedPersistenceRef = useRef(false);
  useEffect(() => {
    if (hasRequestedPersistenceRef.current || isPersisted) return;

    const audio = document.querySelector('audio');
    if (!audio) return;

    const handleFirstPlay = () => {
      if (!hasRequestedPersistenceRef.current) {
        hasRequestedPersistenceRef.current = true;
        requestPersistence();
        audio.removeEventListener('play', handleFirstPlay);
      }
    };

    audio.addEventListener('play', handleFirstPlay);
    return () => audio.removeEventListener('play', handleFirstPlay);
  }, [isPersisted, requestPersistence]);

  // Only show full loading screen on initial load (no episode data yet)
  if (initialLoading && !episode) {
    return (
      <div className="h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="text-center">
          <div
            className="inline-block w-10 h-10 border-3 border-[var(--color-border)]
                        border-t-[var(--color-primary)] rounded-full animate-spin"
          />
          <p className="mt-3 text-sm text-[var(--color-text-muted)]">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !episode || !show || !stream) {
    return (
      <div className="h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="text-center p-6">
          <h1 className="text-xl font-semibold text-[var(--color-text)] mb-2">
            Episode Not Available
          </h1>
          <p className="text-sm text-[var(--color-text-muted)]">
            {error || 'This episode could not be found.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[var(--color-bg)] overflow-hidden" style={{ height: '100dvh' }}>
      {/* Skip link for accessibility */}
      <a href="#main-player" className="skip-link">
        Skip to player
      </a>

      {/* Compact header with search and PWA navigation - hidden when nothing to show */}
      {(stream.show_search !== false || (isPWAMode && stream.rssfilename)) && (
      <header
        className="flex-shrink-0 z-40 bg-[var(--color-surface)] border-b border-[var(--color-border)] px-3 py-2"
        style={{ paddingTop: 'max(0.5rem, var(--safe-area-inset-top))' }}
      >
        <div className="flex items-center gap-2">
          {/* Search bar - takes remaining space (hidden if show_search disabled) */}
          {stream.show_search !== false && (
          <div className="flex-1 min-w-0">
            <SearchBar
              ref={searchInputRef}
              value={searchTerm}
              onChange={handleSearchChange}
              onSubmit={doSearch}
              placeholder={`Search ${stream.streamname}`}
              isLoading={isSearching}
            />
          </div>
          )}

          {/* PWA Navigation buttons - only show in PWA mode */}
          {isPWAMode && stream.rssfilename && (
            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Browse Episodes */}
              <button
                onClick={() => navigate(`/browse?stream=${stream.rssfilename}`)}
                className="flex items-center justify-center w-9 h-9 rounded-full
                          text-[var(--color-text-muted)] hover:text-[var(--color-text)]
                          hover:bg-[var(--color-surface-elevated)]
                          transition-colors"
                aria-label="Browse episodes"
                title="Browse episodes"
              >
                <HiSquares2X2 size={20} />
              </button>

              {/* Manage Notifications */}
              <button
                onClick={() => navigate(`/notifications?stream=${stream.rssfilename}`)}
                className="flex items-center justify-center w-9 h-9 rounded-full
                          text-[var(--color-text-muted)] hover:text-[var(--color-text)]
                          hover:bg-[var(--color-surface-elevated)]
                          transition-colors relative"
                aria-label="Manage notifications"
                title="Manage notifications"
              >
                <HiBell size={20} />
                {/* Badge showing followed count */}
                {followedShows.length > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 flex items-center justify-center
                                 bg-[var(--color-primary)] text-white text-[9px] font-bold rounded-full">
                    {followedShows.length > 9 ? '9+' : followedShows.length}
                  </span>
                )}
              </button>
            </div>
          )}
        </div>
      </header>
      )}

      {/* Search Results Overlay - glass effect */}
      {showResults && (
        <div className="fixed inset-0 z-50 overflow-auto
                        bg-[var(--color-bg)]/80 backdrop-blur-xl">
          <div className="px-4 pb-4">
            {/* Sticky header */}
            <div className="flex justify-between items-center py-3 sticky top-0 z-10
                          bg-[var(--color-surface)]/70 backdrop-blur-md
                          border-b border-[var(--color-border)]/50
                          -mx-4 px-4">
              <h2 className="text-sm font-semibold text-[var(--color-text)]">
                Search Results
              </h2>
              <button
                onClick={handleCloseResults}
                className="text-xs font-medium px-3 py-1.5 rounded-full
                           bg-[var(--color-surface-elevated)]/80
                           text-[var(--color-text-secondary)]
                           hover:text-[var(--color-text)]
                           hover:bg-[var(--color-surface-elevated)]
                           transition-colors
                           focus:outline-none focus-visible:ring-2
                           focus-visible:ring-[var(--color-primary)]"
              >
                Close
              </button>
            </div>
            <SearchResults
              results={searchResults}
              isLoading={isSearching}
              searchTerm={searchTerm}
              fallbackImage={stream.image}
              queuedEpisodeIds={queue.map(ep => ep.episodeid)}
              onAddToQueue={handleAddToQueue}
              onNavigate={navigateToEpisode}
            />
          </div>
        </div>
      )}

      {/* Main content area - queue + player + sidebar in wide mode */}
      <div className="flex-1 flex min-h-0 overflow-hidden relative">
        {/* Immersive blurred background - extends behind sidebars */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          {/* Current background image - always visible as base layer */}
          {currentBgImage && (
            <img
              src={currentBgImage}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover scale-110 blur-3xl opacity-40"
            />
          )}
          {/* Next background image - fades in on top during transition */}
          {nextBgImage && (
            <img
              src={nextBgImage}
              alt=""
              aria-hidden="true"
              className={`absolute inset-0 w-full h-full object-cover scale-110 blur-3xl
                         transition-opacity duration-500 ease-in-out
                         ${bgImageLoaded ? 'opacity-40' : 'opacity-0'}`}
            />
          )}
          {/* Tint wash - full coverage colour layer */}
          <div
            className="absolute inset-0 opacity-20"
            style={{ backgroundColor: 'var(--color-tint)' }}
          />
          {/* Gradient overlays */}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(180deg,
                var(--color-bg) 0%,
                transparent 25%,
                transparent 75%,
                var(--color-bg) 100%)`,
            }}
          />
          <div
            className="absolute inset-0 opacity-50"
            style={{
              background: `radial-gradient(ellipse at center, var(--color-tint) 0%, transparent 70%)`,
            }}
          />
        </div>

        {/* Left Sidebar - Queue (visible on wide screens, hidden in panel modes) */}
        {!isPanelMode && stream.show_sidebar !== false && (
        <aside className="hidden lg:flex lg:flex-col lg:w-[280px] flex-shrink-0 z-10
                          bg-[var(--color-surface)]/60 backdrop-blur-xl
                          border-r border-[var(--color-border)]/30
                          overflow-hidden">
          <Queue
            queue={queue}
            currentEpisodeId={episode.episodeid}
            fallbackImage={stream.image}
            onRemove={handleRemoveFromQueue}
            onPlay={handlePlayFromQueue}
            onClear={handleClearQueue}
            onReorder={handleReorderQueue}
          />
        </aside>
        )}

        {/* Main content - Shows panel overlays Player (Player stays mounted for continuous playback) */}
        <main
          id="main-player"
          className="flex-1 flex flex-col min-h-0 overflow-hidden relative z-10"
        >
          {/* Pre-roll: waiting to start — show play button */}
          {prerollParam && !prerollDone && !prerollPlaying && episode && (
            <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-black/90 backdrop-blur-md">
              <button
                onClick={startPreroll}
                className="w-20 h-20 rounded-full bg-[var(--color-primary)] flex items-center justify-center mb-4 hover:scale-105 active:scale-95 transition-transform"
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
              </button>
              <p className="text-[var(--color-text)] text-base font-medium">Play Highlight</p>
              <p className="text-[var(--color-text-muted)] text-sm mt-1">A short message will play first</p>
            </div>
          )}

          {/* Pre-roll: ad is playing */}
          {prerollPlaying && (
            <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-black/90 backdrop-blur-md">
              <div className="w-10 h-10 border-3 border-[var(--color-border)] border-t-[var(--color-primary)] rounded-full animate-spin mb-4" />
              <p className="text-[var(--color-text)] text-base font-medium">Playing message...</p>
              <p className="text-[var(--color-text-muted)] text-sm mt-1">Your audio will start shortly</p>
            </div>
          )}

          {/* Player always mounted to preserve audio playback.
              In panel modes: absolute+invisible so audio element stays alive */}
          <div className={isPanelMode ? 'absolute inset-0 opacity-0 pointer-events-none -z-10' : 'flex-1 flex flex-col min-h-0'}>
            <Player
              key={playerIsLive ? `live-${stream.streamid}` : episode.episodeid}
              episode={episode}
              stream={stream}
              presenter={presenter}
              initialTime={playerIsLive ? undefined : effectiveStartTime}
              endTime={playerIsLive ? undefined : endTime}
              titleOverride={titleOverride || undefined}
              imageOverride={imageOverride || undefined}
              isLive={playerIsLive}
              autoplay={(shouldAutoplay || playerIsLive) && prerollDone}
              onSearchFocus={handleFocusSearch}
              onPrevTrack={playerIsLive ? undefined : handlePrevTrack}
              onNextTrack={playerIsLive ? undefined : handleNextTrack}
              hasPrevTrack={playerIsLive ? false : hasPrevTrack}
              hasNextTrack={playerIsLive ? false : true}
              onEnded={playerIsLive ? undefined : handleEpisodeEnded}
              onShare={playerIsLive || stream.show_share === false ? undefined : () => setIsShareOpen(true)}
              onSubscribe={stream.show_share === false ? undefined : () => setIsRssOpen(true)}
              onInfo={playerIsLive ? undefined : () => setIsInfoOpen(true)}
              showSubscribe={stream.show_share !== false}
              onOpenQueue={stream.show_sidebar !== false ? openQueueDrawer : undefined}
              onOpenEpisodes={stream.show_sidebar !== false ? openEpisodesDrawer : undefined}
              queueCount={queue.length}
              onShowInfo={playerIsLive ? setShowInfoData : undefined}
              onImageChange={playerIsLive ? setLiveShowImage : undefined}
            />
          </div>
          {isShowsMode && <ShowsPanel stream={stream} />}
          {isScheduleMode && (
            <SchedulePanel
              stream={stream}
              onNavigate={navigateToEpisode}
              onGoLive={stream.live_enabled === true && stream.rssfilename
                ? () => navigate(`/?live=true&stream=${stream.rssfilename}`)
                : undefined}
              fallbackImage={stream.image}
              isPWAMode={isPWAMode}
              isFollowing={isFollowing}
              onFollow={handleFollowShow}
              onUnfollow={handleUnfollowShow}
              onCurrentShowChange={setCurrentlyLiveShowId}
            />
          )}
        </main>

        {/* Right Sidebar - Recent episodes (visible on medium+ screens, hidden in panel modes) */}
        {!isPanelMode && stream.show_sidebar !== false && (
        <aside className="hidden md:flex md:flex-col md:w-[280px] flex-shrink-0 z-10
                          bg-[var(--color-surface)]/60 backdrop-blur-xl
                          border-l border-[var(--color-border)]/30
                          overflow-hidden">
          <Sidebar
            stream={stream}
            show={show}
            currentEpisodeId={episode.episodeid}
            queuedEpisodeIds={queue.map(ep => ep.episodeid)}
            onAddToQueue={handleAddToQueue}
            onNavigate={navigateToEpisode}
            isLiveMode={isLiveMode}
            historyItems={history}
            onRemoveFromHistory={removeFromHistory}
          />
        </aside>
        )}

      </div>

      {/* Bottom bar - mode toggle + content attribution */}
      <footer
        className="flex-shrink-0 bg-[var(--color-surface)]/60 backdrop-blur-xl
                  border-t border-[var(--color-border)]/30"
        style={{ paddingBottom: 'var(--safe-area-inset-bottom)' }}
      >
        <div className="flex items-center justify-between px-4 py-2">
          {/* Mode toggle - show if any toggle option is enabled */}
          {(stream.live_enabled === true || stream.show_shows === true || stream.show_schedule === true) ? (
          <div className="flex items-center bg-[var(--color-surface-elevated)]/60 rounded-full p-0.5">
            {stream.live_enabled === true && (
            <button
              onClick={() => {
                if (!isLiveMode && stream.rssfilename) {
                  navigate(`/?live=true&stream=${stream.rssfilename}`);
                }
              }}
              disabled={!(stream.live_url || stream.url)}
              className={`px-3 py-1 text-[10px] font-semibold rounded-full transition-all
                         ${isLiveMode
                           ? 'bg-red-500 text-white'
                           : (stream.live_url || stream.url)
                             ? 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                             : 'text-[var(--color-text-muted)]/50 cursor-not-allowed'
                         }`}
              title={(stream.live_url || stream.url) ? 'Switch to live radio' : 'Live stream not available'}
            >
              {isLiveMode && <span className="inline-block w-1.5 h-1.5 bg-white rounded-full mr-1.5 animate-pulse" />}
              LIVE
            </button>
            )}
            <button
              onClick={async () => {
                if (isPanelMode || isLiveMode) {
                  if (latestEpisodeId) {
                    navigate(`/${latestEpisodeId}`);
                  } else {
                    try {
                      const recentEpisodes = await getRecentStationEpisodes(stream.streamid, 1);
                      if (recentEpisodes.length > 0) {
                        navigate(`/${recentEpisodes[0].episodeid}`);
                      }
                    } catch { /* ignore */ }
                  }
                }
              }}
              className={`px-3 py-1 text-[10px] font-semibold rounded-full transition-all
                         ${!isLiveMode && !isPanelMode
                           ? 'bg-[var(--color-primary)] text-white'
                           : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                         }`}
              title="Switch to on-demand episodes"
            >
              ON DEMAND
            </button>
            {stream.show_shows === true && stream.rssfilename && (
              <button
                onClick={() => {
                  if (!isShowsMode) {
                    navigate(`/shows?stream=${stream.rssfilename}`);
                  }
                }}
                className={`px-3 py-1 text-[10px] font-semibold rounded-full transition-all
                           ${isShowsMode
                             ? 'bg-[var(--color-primary)] text-white'
                             : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                           }`}
                title="Browse shows"
              >
                SHOWS
              </button>
            )}
            {stream.show_schedule === true && stream.rssfilename && (
              <button
                onClick={() => {
                  if (!isScheduleMode) {
                    navigate(`/schedule?stream=${stream.rssfilename}`);
                  }
                }}
                className={`px-3 py-1 text-[10px] font-semibold rounded-full transition-all
                           ${isScheduleMode
                             ? 'bg-[var(--color-primary)] text-white'
                             : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                           }`}
                title="View schedule"
              >
                SCHEDULE
              </button>
            )}
          </div>
          ) : <div />}

          {/* Content attribution - right aligned, compact when many toggles */}
          <span className="text-[10px] text-[var(--color-text-muted)] truncate ml-2">
            {(stream.live_enabled === true ? 1 : 0)
              + (stream.show_shows === true ? 1 : 0)
              + (stream.show_schedule === true ? 1 : 0) >= 2
              ? stream.streamname
              : `Content by ${stream.streamname}`}
          </span>
        </div>
      </footer>

      {/* Share Modal */}
      <ShareModal
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        episodeId={episode.episodeid}
        currentTime={currentTime}
        baseUrl={stream?.player_url || undefined}
      />

      {/* Info Modal */}
      <InfoModal
        isOpen={isInfoOpen}
        onClose={() => setIsInfoOpen(false)}
        episode={episode}
        show={show}
        stream={stream}
        presenter={presenter}
        duration={duration}
      />

      {/* RSS/Subscribe Modal */}
      <RssModal
        isOpen={isRssOpen}
        onClose={() => setIsRssOpen(false)}
        show={show}
      />

      {/* Show Info Modal (live mode - On Air / Up Next) */}
      <ShowInfoModal
        info={showInfoData}
        streamImage={stream.image}
        onClose={() => setShowInfoData(null)}
      />

      {/* Mobile Queue Drawer - full screen overlay, slides in from left */}
      {queueDrawerState !== 'closed' && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className={`absolute inset-0 bg-black/50 backdrop-blur-sm
                       ${queueDrawerState === 'open' ? 'backdrop-fade-in' : ''}
                       ${queueDrawerState === 'closing' ? 'transition-opacity duration-200 opacity-0' : ''}`}
            onClick={closeQueueDrawer}
          />
          {/* Drawer */}
          <aside
            className={`relative w-[300px] max-w-[85vw] h-full flex flex-col overflow-hidden
                       bg-[var(--color-surface)]/70 backdrop-blur-xl
                       border-r border-[var(--color-border)]/30 shadow-xl
                       ${queueDrawerState === 'open' ? 'drawer-left-open' : ''}
                       ${queueDrawerState === 'closing' ? 'transition-transform duration-200 ease-out -translate-x-full' : ''}`}
            onTouchStart={queueSwipe.handlers.onTouchStart}
            onTouchMove={queueSwipe.handlers.onTouchMove}
            onTouchEnd={queueSwipe.handlers.onTouchEnd}
          >
            {/* Blurred album art background with crossfade */}
            <div className="absolute inset-0 z-0 overflow-hidden">
              <div className="absolute inset-0 opacity-15" style={{ backgroundColor: 'var(--color-tint)' }} />
              {currentBgImage && (
                <img
                  src={currentBgImage}
                  alt=""
                  aria-hidden="true"
                  className={`absolute inset-0 w-full h-full object-cover scale-125 blur-3xl
                             transition-opacity duration-500 ease-in-out
                             ${isBgTransitioning && bgImageLoaded ? 'opacity-0' : 'opacity-30'}`}
                />
              )}
              {nextBgImage && (
                <img
                  src={nextBgImage}
                  alt=""
                  aria-hidden="true"
                  className={`absolute inset-0 w-full h-full object-cover scale-125 blur-3xl
                             transition-opacity duration-500 ease-in-out
                             ${bgImageLoaded ? 'opacity-30' : 'opacity-0'}`}
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-surface)]/50 to-[var(--color-surface)]/80" />
            </div>
            <div className="relative z-10 flex items-center justify-between px-3 py-2 border-b border-[var(--color-border)]/30">
              <span className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase">
                Queue
              </span>
              <button
                onClick={closeQueueDrawer}
                className="p-1.5 rounded-full hover:bg-[var(--color-surface-elevated)]
                           transition-colors focus:outline-none"
                aria-label="Close queue"
              >
                <HiXMark size={18} className="text-[var(--color-text-muted)]" />
              </button>
            </div>
            <div className="relative z-10 flex-1 overflow-hidden">
              <Queue
                queue={queue}
                currentEpisodeId={episode.episodeid}
                fallbackImage={stream.image}
                onRemove={handleRemoveFromQueue}
                onPlay={(id) => {
                  handlePlayFromQueue(id);
                  closeQueueDrawer();
                }}
                onClear={handleClearQueue}
                onReorder={handleReorderQueue}
              />
            </div>
          </aside>
        </div>
      )}

      {/* Mobile Episodes Drawer - full screen overlay, slides in from right */}
      {episodesDrawerState !== 'closed' && (
        <div className="md:hidden fixed inset-0 z-50 flex justify-end">
          {/* Backdrop */}
          <div
            className={`absolute inset-0 bg-black/50 backdrop-blur-sm
                       ${episodesDrawerState === 'open' ? 'backdrop-fade-in' : ''}
                       ${episodesDrawerState === 'closing' ? 'transition-opacity duration-200 opacity-0' : ''}`}
            onClick={closeEpisodesDrawer}
          />
          {/* Drawer */}
          <aside
            className={`relative w-[300px] max-w-[85vw] h-full flex flex-col overflow-hidden
                       bg-[var(--color-surface)]/70 backdrop-blur-xl
                       border-l border-[var(--color-border)]/30 shadow-xl
                       ${episodesDrawerState === 'open' ? 'drawer-right-open' : ''}
                       ${episodesDrawerState === 'closing' ? 'transition-transform duration-200 ease-out translate-x-full' : ''}`}
            onTouchStart={episodesSwipe.handlers.onTouchStart}
            onTouchMove={episodesSwipe.handlers.onTouchMove}
            onTouchEnd={episodesSwipe.handlers.onTouchEnd}
          >
            {/* Blurred album art background with crossfade */}
            <div className="absolute inset-0 z-0 overflow-hidden">
              <div className="absolute inset-0 opacity-15" style={{ backgroundColor: 'var(--color-tint)' }} />
              {currentBgImage && (
                <img
                  src={currentBgImage}
                  alt=""
                  aria-hidden="true"
                  className={`absolute inset-0 w-full h-full object-cover scale-125 blur-3xl
                             transition-opacity duration-500 ease-in-out
                             ${isBgTransitioning && bgImageLoaded ? 'opacity-0' : 'opacity-30'}`}
                />
              )}
              {nextBgImage && (
                <img
                  src={nextBgImage}
                  alt=""
                  aria-hidden="true"
                  className={`absolute inset-0 w-full h-full object-cover scale-125 blur-3xl
                             transition-opacity duration-500 ease-in-out
                             ${bgImageLoaded ? 'opacity-30' : 'opacity-0'}`}
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-surface)]/50 to-[var(--color-surface)]/80" />
            </div>
            <div className="relative z-10 flex items-center justify-between px-3 py-2 border-b border-[var(--color-border)]/30">
              <span className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase">Episodes</span>
              <button
                onClick={closeEpisodesDrawer}
                className="p-1.5 rounded-full hover:bg-[var(--color-surface-elevated)]
                           transition-colors focus:outline-none"
                aria-label="Close episodes"
              >
                <HiXMark size={18} className="text-[var(--color-text-muted)]" />
              </button>
            </div>
            <div className="relative z-10 flex-1 overflow-hidden">
              <Sidebar
                stream={stream}
                show={show}
                currentEpisodeId={episode.episodeid}
                queuedEpisodeIds={queue.map(ep => ep.episodeid)}
                onAddToQueue={handleAddToQueue}
                onNavigate={(id) => {
                  navigateToEpisode(id);
                  closeEpisodesDrawer();
                }}
                isLiveMode={isLiveMode}
                historyItems={history}
                onRemoveFromHistory={removeFromHistory}
              />
            </div>
          </aside>
        </div>
      )}

      {/* PWA Install Prompt */}
      {isPromptVisible && stream?.enable_pwa !== false && (
        <InstallPrompt
          stationName={stream?.streamname}
          stationImage={stream?.image}
          isIOS={isIOS}
          onInstall={install}
          onDismiss={dismissPrompt}
        />
      )}
    </div>
  );
}

export default App;
